/**
 * Il pannello della decisione: fisso a destra, e non si chiude.
 *
 * **Fisso perche' e' il riferimento della mano.** L'operatore batte i tasti
 * senza guardare, e guarda qui solo per confermare che il modo acceso e' quello
 * che credeva. Un pannello che si sposta o cambia larghezza costringe a
 * rileggere ogni volta, e rileggere per seicento candidati e' il costo vero.
 *
 * **Non si chiude, ed e' una scelta contro il concetto vecchio.** Il tasto che
 * lo nascondeva c'era; e' stato tolto, non reso inoffensivo. Nascondere lo stato
 * della decisione mentre si confrontano immagine e overlay significa decidere
 * senza sapere in che modo si e' — cioe' il difetto che tutta questa schermata
 * esiste per evitare.
 *
 * **Ogni tasto ha qui il suo comando visibile, e i due producono la stessa
 * azione.** Non due strade parallele: il bottone manda la stessa `Action` che
 * manderebbe il tasto, e chi decide che cosa farne e' il Cockpit. Due strade che
 * divergono sono peggio di una, perche' la differenza si scopre dal giornale.
 */
import { Hint } from '../carta/Hint'
import { Margin, Severity } from '../design/indicators'
import { RANKS, rankOf } from '../design/scales'
import { decimal, metres, moment, notMeasured } from '../format'
import type { MessageKey } from '../i18n/messages'
import { useT } from '../i18n/LocaleProvider'
import { explainReason } from '../i18n/explain'
import type { Action, Mode } from '../keys'
import { clicking, mutating, repeated } from '../keys'
import type { IntentState } from '../outbox'
import type { Shape } from '../Viewer'
import { INTENT_TONES, IN_ATTESA, leaseLabel } from './state'
import type { Candidate, LastVerdict, Reason } from '../types'

/**
 * Da che cosa si e' appena usciti, se da qualcosa.
 *
 * Il disegno del riquadro sta qui accanto ai modi della tastiera perche' per chi
 * lavora e' la stessa cosa: uno stato in cui il gesto successivo significa
 * un'altra cosa. `Esc` li spegne insieme, e insieme vanno detti.
 */
export type Exited = Exclude<Mode, 'normale'> | 'disegno'

export interface DecisionPanelProps {
  candidate: Candidate | null
  reasons: Reason[]
  mode: Mode
  /** Il modo da cui si e' appena usciti, per dirlo invece di tacere. */
  exited: Exited | null
  /** Che fine ha fatto la decisione su questo candidato. */
  intent: IntentState | null
  /** Perche' l'ultimo gesto non e' diventato un verdetto. */
  refusal: string | null
  /** Perche' non si puo' decidere affatto, se non si puo'. */
  blocked: string | null
  /** Il verdetto gia' dato, quando il candidato e' aperto per identita' e non
   *  dalla coda: si legge al posto dello stato dell'intento. */
  decided?: LastVerdict | null
  /** Chi tiene questa sessione: serve a dire «in mano a te» invece del nome. */
  mine?: string | null
  undoSeconds: number
  /** Ai primi verdetti: la nota che dice il patto dell'annullamento (GI-281). */
  novice?: boolean
  /** Che cosa si sta disegnando, o nulla. */
  drawing: Shape | null
  /** La forma ridisegnata che parte con la conferma, se c'e'. */
  redrawn: Shape | null
  /** Vero quando la trovata manuale ha un'uscita da questa schermata. */
  manual: boolean
  /** Vero quando il candidato ha un elemento e la scheda ha un'uscita. */
  element: boolean
  /** Vero quando il banco guarda senza sessione: e' il solo caso in cui si
   *  offre di aprirne una qui. */
  observing?: boolean
  /** Apre una sessione da questo banco, con i suoi lease, senza passare dal
   *  pool. Assente fuori dall'instradamento (GI-246). */
  onSession?: () => void
  /** Gli altri fotogrammi dello stesso elemento: l'uscita verso il
   *  fotogrammario filtrato. Assente senza elemento o fuori dall'instradamento
   *  (GI-248). */
  onFrames?: () => void
  onAction: (action: Action) => void
}

/** Un comando del pannello. Quarantaquattro pixel di bersaglio: e' la misura
 *  sotto la quale un puntatore impreciso sbaglia bottone, e qui un bottone
 *  sbagliato e' un verdetto sbagliato.
 *
 *  **E' il confine dove la ripetizione della tastiera si ferma anche per i
 *  comandi.** Ci passano tutti: conferma, falso positivo, rinvio, trovata
 *  manuale, annullamento, ranghi e motivi. La tela restituisce l'invio al
 *  bottone che ha il fuoco — e deve, altrimenti il comando non si attiva a
 *  tastiera — quindi il fermo della tela non lo vede: qui si legge la stessa
 *  politica (`repeated`), e non una seconda scritta a parte. */
function Command({
  action,
  label,
  keys,
  disabled,
  tone,
  variant,
  onAction,
}: {
  action: Action
  label: string
  keys: string
  disabled: boolean
  tone?: string
  /** Il peso del comando nella gerarchia (GI-266): il verdetto primario, i
   *  secondari, la cella di un rango. Senza, un comando come gli altri. */
  variant?: 'primaria' | 'secondaria' | 'rango' | 'piccola'
  onAction: (action: Action) => void
}): JSX.Element {
  return (
    <button
      type="button"
      className={variant ? `cockpit-azione cockpit-azione-${variant}` : 'cockpit-azione'}
      disabled={disabled}
      style={tone ? { color: `var(--${tone})` } : undefined}
      onKeyDown={(event) => {
        // L'attivazione dell'invio e' l'azione predefinita del `keydown`:
        // annullarla sulla ripetizione e' il clic che non parte. La prima
        // pressione resta intera, lo spazio non si tocca (si attiva al
        // rilascio, e tenerlo premuto da' un clic solo).
        if (clicking(event) && repeated(event, action)) event.preventDefault()
      }}
      onClick={() => onAction(action)}
    >
      <span className="cockpit-tasto" aria-hidden="true">
        {keys}
      </span>
      {variant === 'rango' && label.includes(' · ') ? (
        // La cella di un rango va su due righe per disegno — la sigla e il
        // suo nome — e non per caso: una parola lunga («Moderate») che si
        // spezzava da sola faceva una cella piu' alta delle altre.
        <span className="cockpit-rango-etichetta">
          <span className="cockpit-rango-sigla">{label.split(' · ')[0]}</span>
          <span className="cockpit-rango-nome">{label.split(' · ').slice(1).join(' · ')}</span>
        </span>
      ) : (
        label
      )}
    </button>
  )
}

/** Come si nomina la sorgente di un candidato.
 *
 * Il modello e la mano non si giudicano allo stesso modo — una proposta del
 * modello si controlla, una dichiarazione manuale si e' gia' controllata — e il
 * carico lo dice da sempre senza che si vedesse. I nomi che il servizio non
 * conosce si mostrano come sono: tradurne uno a caso sarebbe peggio. */
function sourceLabel(
  source: string,
  t: (key: MessageKey) => string,
): string {
  if (source === 'model') return t('cockpit.sourceModel')
  if (source === 'manual') return t('cockpit.sourceManual')
  if (source === 'import') return t('cockpit.sourceImport')
  return source
}

/**
 * Che cosa dice la regione viva del pannello, in ordine di urgenza.
 *
 * **Una voce sola, e un posto solo che la decide.** Il rifiuto dell'ultimo
 * gesto viene prima di tutto perche' risponde a cio' che la mano ha appena
 * fatto; poi la ragione per cui non si puo' decidere affatto; poi il modo
 * acceso, che dice **che cosa fara' la cifra successiva** — senza, il modo
 * viveva in una classe e in un nome di gruppo, e chi ascolta batteva la cifra
 * credendo di confermare.
 *
 * **E l'uscita da un modo e' un annuncio, non un silenzio.** Svuotare la regione
 * viva non dice niente a chi ascolta: il modo si spegneva e l'operatore restava
 * a credere che la cifra successiva dichiarasse ancora un rango — cioe' lo stesso
 * verdetto sbagliato che l'annuncio dell'entrata esiste per evitare, preso
 * dall'altro capo. L'uscita sta sotto l'entrata: quando un modo e' acceso vince
 * lui, e le due voci non si sovrappongono mai.
 */
function voice({
  refusal,
  blocked,
  mode,
  exited,
  drawing,
  redrawn,
  t,
}: {
  refusal: string | null
  blocked: string | null
  mode: Mode
  exited: Exited | null
  drawing: Shape | null
  redrawn: Shape | null
  t: (key: MessageKey) => string
}): string {
  if (refusal !== null) return refusal
  if (blocked !== null) return blocked
  if (drawing === 'polygon') return t('cockpit.drawPolygon')
  if (drawing === 'box') return t('cockpit.drawBox')
  if (mode === 'rango') {
    return t('cockpit.modeRank')
  }
  if (mode === 'motivo') {
    return t('cockpit.modeReason')
  }
  if (exited === 'rango') {
    return t('cockpit.exitedRank')
  }
  if (exited === 'motivo') {
    return t('cockpit.exitedReason')
  }
  if (exited === 'disegno') {
    return t('cockpit.exitedDraw')
  }
  if (redrawn === 'polygon') return t('cockpit.polygonCorrected')
  if (redrawn === 'box') return t('cockpit.boxCorrected')
  return ''
}

export function DecisionPanel({
  candidate,
  reasons,
  mode,
  exited,
  intent,
  refusal,
  blocked,
  decided = null,
  mine = null,
  undoSeconds,
  novice = false,
  drawing,
  redrawn,
  manual,
  element,
  observing = false,
  onSession,
  onFrames,
  onAction,
}: DecisionPanelProps): JSX.Element {
  const t = useT()
  const locked = blocked !== null || candidate === null

  /* Un giudizio solo su che cosa e' spento, e legge lo stesso elenco del
   * gestore dei tasti (`mutating`): erano due giudizi separati, e divergevano —
   * il bottone risultava spento e lo stesso verdetto passava da tastiera. */
  function refused(action: Action): boolean {
    if (action?.kind === 'manual') return !manual
    if (action?.kind === 'element') return !element
    if (action?.kind === 'undo') return blocked !== null
    return mutating(action) ? locked : false
  }

  return (
    <aside className="cockpit-decisione" aria-label={t('cockpit.decision')}>
      <h2 className="solo-lettori">{t('cockpit.decision')}</h2>

      {candidate && (
        <div className="cockpit-candidato">
          {/* **Prima il codice e il rango, grandi** (GI-266): sono le due cose
              che l'operatore confronta col riquadro sul fotogramma, e a 12 px
              stavano grandi come l'etichetta «LEASE». Il rango porta glifo,
              nome e tono, mai uno solo dei tre. */}
          <p className="cockpit-identita">
            <span className="cockpit-codice-grande">{candidate.code}</span>
            <Severity level={candidate.level} />
            {/* Il significato del rango stava solo nel `title=` del grado: per
                chi tocca, tabula o ascolta non c'era (GI-281). */}
            <Hint about="rank" />
          </p>
          {/* **Il titolo e' il significato, non il codice.** E' la riga che si
              legge seicento volte in un turno, e «D10» non dice a nessuno che
              sotto il filo c'e' una pianta da tagliare.

              Quando il catalogo non descrive quel codice — succede per i sette
              fuori dal § 7.3 — il titolo torna a essere il codice: un titolo
              vuoto e' peggio di un codice. */}
          <h3>{candidate.description === '' ? candidate.code : candidate.description}</h3>
          {candidate.confidence !== null && (
            <p className="tenue cockpit-confidenza">
              {t('cockpit.confidenceOf', { n: decimal(candidate.confidence, 2) })}
            </p>
          )}
          {candidate.measured_distance_m !== null && (
            // La plancia delle misure: misurata, norma, margine, in tre celle
            // con la loro didascalia. Il margine e' cio' che si giudica, la
            // misura e' cio' che si e' misurato, e chi rifa' il conto a mano
            // ha bisogno di entrambi — e li trova sempre nella stessa cella.
            <dl className="cockpit-plancia t-num">
              <div>
                <dt>{t('cockpit.measured')}</dt>
                <dd>{metres(candidate.measured_distance_m)}</dd>
              </div>
              <div>
                <dt>{t('cockpit.norm')}</dt>
                <dd>
                  {candidate.minimum_distance_m === null
                    ? notMeasured()
                    : metres(candidate.minimum_distance_m)}
                </dd>
              </div>
              <div className="cockpit-plancia-margine">
                <dt>
                  Δ {t('cockpit.marginLabel')}
                  <Hint about="margin" />
                </dt>
                <dd>
                  <Margin
                    measured={candidate.measured_distance_m}
                    minimum={candidate.minimum_distance_m}
                  />
                </dd>
              </div>
            </dl>
          )}
          {!candidate.exportable && (
            // Va detto prima che ci spenda tempo, non alla validazione della
            // consegna: sul pacchetto reale e' successo alla prima violazione.
            <p className="cockpit-nota">{t('cockpit.notExportable')}</p>
          )}
        </div>
      )}

      {/*
        Una regione viva sola per il pannello, e una funzione sola a deciderne
        la voce: qui finisce tutto cio' che risponde all'ultimo gesto — perche'
        non ha confermato, perche' non si puo' decidere, che cosa fara' la
        prossima cifra. Nessun comando dentro: una regione viva che contiene
        bottoni li rilegge tutti a ogni cambiamento.
      */}
      <p className="cockpit-vivo" role="status" aria-live="polite">
        {voice({ refusal, blocked, mode, exited, drawing, redrawn, t })}
      </p>

      {/*
        Un collegamento incollato da un collega apriva il banco in sola
        osservazione e mandava al pool a riaprire la coda: due schermate per
        arrivare dove si era gia'. Qui si apre la sessione sul posto — la
        stessa apertura del pool, con gli stessi lease — e l'indirizzo riceve
        la sessione come se si fosse entrati da li' (GI-246). Fuori dalla
        regione viva: un bottone dentro verrebbe riletto a ogni cambiamento.
      */}
      {observing && onSession !== undefined && (
        <button type="button" className="cockpit-azione cockpit-qui" onClick={onSession}>
          {t('cockpit.startHere')}
        </button>
      )}

      {/* **I tre verdetti, grandi, e il primo e' primario** (GI-266). Sono i
          gesti che chiudono nove candidati su dieci: A conferma la proposta,
          D e' il rifiuto piu' frequente, F rimanda. Tutto il resto — altro
          rango, altro motivo, dichiarazione a mano, scheda, annullamento —
          e' secondario e sta sotto, piu' piccolo. Prima erano sei comandi
          dello stesso peso, e l'occhio li rileggeva tutti ogni volta. */}
      {candidate && (
        <div className="cockpit-comandi">
          <Command
            action={{ kind: 'confirm', rank: null }}
            label={t('cockpit.confirm')}
            keys="A"
            variant="primaria"
            disabled={refused({ kind: 'confirm', rank: null })}
            onAction={onAction}
          />
          <Command
            action={{ kind: 'falsePositive' }}
            label={t('cockpit.falsePositive')}
            keys="D"
            disabled={refused({ kind: 'falsePositive' })}
            onAction={onAction}
          />
          <Command
            action={{ kind: 'defer' }}
            label={t('cockpit.defer')}
            keys="F"
            disabled={refused({ kind: 'defer' })}
            onAction={onAction}
          />
        </div>
      )}

      {candidate && (
        <fieldset
          className={mode === 'rango' ? 'cockpit-scala acceso' : 'cockpit-scala'}
          // Un gruppo e non un elenco: dopo «S» la cifra successiva sceglie qui
          // dentro, e chi usa un lettore di schermo deve sentire di essere
          // entrato in una scelta.
          aria-label={mode === 'rango' ? t('cockpit.chooseRank') : t('cockpit.ranks')}
        >
          <legend>
            {mode === 'rango' ? t('cockpit.chooseRank') : t('cockpit.confirmRank')}
            <span className="cockpit-legenda-tasto"> · {t('cockpit.rankHint')}</span>
          </legend>
          <div className="cockpit-scala-celle">
            {RANKS.filter((one) => one.name !== 'S0').map((one, index) => (
              <Command
                key={one.name}
                action={{ kind: 'confirm', rank: index + 1 }}
                label={`${one.name} · ${one.meaning}`}
                keys={String(index + 1)}
                variant="rango"
                disabled={refused({ kind: 'confirm', rank: index + 1 })}
                tone={one.tone}
                onAction={onAction}
              />
            ))}
          </div>
        </fieldset>
      )}

      {candidate && (
        <div className="cockpit-secondari">
          {/* «R» apre la scelta del motivo: l'elenco dei motivi sta chiuso
              finche' non serve, perche' sette righe dello stesso peso dei
              verdetti erano la meta' del pannello per il gesto meno frequente.
              Il bottone manda la stessa azione del tasto. */}
          <Command
            action={{ kind: 'rejecting' }}
            label={t('cockpit.rejectWithReasonCmd')}
            keys="R"
            variant="secondaria"
            // Entrare nel modo non decide niente, e il tasto lo permette anche a
            // banco fermo: e' la cifra dopo che viene rifiutata, con la ragione.
            disabled={mode === 'motivo'}
            onAction={onAction}
          />
          <Command
            action={{ kind: 'manual' }}
            label={t('cockpit.manual')}
            keys="G"
            variant="secondaria"
            // Spento con la sua ragione, e non assente: un comando che sparisce
            // lascia l'operatore a chiedersi se ha sbagliato a cercarlo.
            disabled={refused({ kind: 'manual' })}
            onAction={onAction}
          />
        </div>
      )}

      {candidate && (
        <fieldset
          className={mode === 'motivo' ? 'cockpit-motivi acceso' : 'cockpit-motivi'}
          aria-label={mode === 'motivo' ? t('cockpit.chooseReason') : t('cockpit.rejectReasons')}
        >
          <legend>{mode === 'motivo' ? t('cockpit.chooseReason') : t('cockpit.rejectWithReason')}</legend>
          {reasons.map((one) => (
            <Command
              key={one.code}
              action={{ kind: 'reject', shortcut: one.shortcut }}
              label={explainReason(t, one.code, one.label)}
              keys={String(one.shortcut)}
              disabled={refused({ kind: 'reject', shortcut: one.shortcut })}
              onAction={onAction}
            />
          ))}
        </fieldset>
      )}

      {candidate && (
        /*
          Il carico portava questi campi da sempre e non si vedevano. La
          sorgente distingue una proposta del modello da una dichiarazione
          fatta a mano — due cose che non si giudicano allo stesso modo —
          l'elemento e' cio' che il committente legge nella consegna, e lo
          stato del lease con la sua scadenza e' il tempo che resta prima che
          il candidato torni disponibile per un collega. In fondo e piccoli
          (GI-266): si consultano, non si leggono seicento volte.
        */
        <dl className="cockpit-dati">
          <dt>{t('cockpit.source')}</dt>
          <dd>{sourceLabel(candidate.source, t)}</dd>
          <dt>{t('cockpit.element')}</dt>
          <dd>
            {elementLabel(candidate) ?? <span className="tenue">{notMeasured()}</span>}
            {onFrames !== undefined && (
              <>
                {' '}
                <button type="button" className="cockpit-link" onClick={onFrames}>
                  {t('cockpit.otherViews')}
                </button>
              </>
            )}
            <span className="cockpit-dati-comando">
              <Command
                action={{ kind: 'element' }}
                label={t('cockpit.openElement')}
                keys="O"
                variant="piccola"
                disabled={refused({ kind: 'element' })}
                onAction={onAction}
              />
            </span>
          </dd>
          <dt>
            {t('cockpit.lease')}
            <Hint about="lease" />
          </dt>
          <dd>
            {leaseLabel(candidate.lease_state, mine !== null && candidate.lease_token != null)}
            {candidate.lease_expires_at != null && (
              <span className="tenue">{t('cockpit.expires', { when: moment(candidate.lease_expires_at) })}</span>
            )}
          </dd>
          <dt>{t('cockpit.decision')}</dt>
          {decided !== null ? (
            <dd className="cockpit-deciso" style={{ color: `var(--${verdictTone(decided)})` }}>
              <span className="glifo" aria-hidden="true">
                {decided.decision === 'confirmed' ? rankOf(decided.final_level).glyph : '✕'}
              </span>{' '}
              {verdictLabel(decided, reasons, t)}
              <span className="tenue">
                {' '}
                {t('cockpit.decidedBy', {
                  who: decided.decided_by ?? notMeasured(),
                  when: moment(decided.decided_at),
                })}
              </span>
            </dd>
          ) : (
            <dd style={{ color: `var(--${(intent ? INTENT_TONES[intent] : IN_ATTESA).tone})` }}>
              <span className="glifo" aria-hidden="true">
                {(intent ? INTENT_TONES[intent] : IN_ATTESA).glyph}
              </span>{' '}
              {(intent ? INTENT_TONES[intent] : IN_ATTESA).label}
            </dd>
          )}
        </dl>
      )}

      {candidate && (
        <p className="cockpit-annulla">
          <Command
            action={{ kind: 'undo' }}
            label={undoSeconds > 0 ? t('cockpit.undoSeconds', { n: undoSeconds }) : t('cockpit.undo')}
            keys="Ctrl+Z"
            variant="piccola"
            disabled={refused({ kind: 'undo' })}
            onAction={onAction}
          />
        </p>
      )}
      {candidate && novice && <p className="cockpit-primi tenue">{t('cockpit.firstVerdicts')}</p>}
    </aside>
  )
}

/** «5825502200 · SEGMENT»: il nome che il committente da' all'elemento, col
 *  tipo. L'UUID interno non si mostra mai — l'operatore non lo riconosce e
 *  non lo ritrova in nessun documento della consegna; per un elemento che
 *  c'e' ma di cui il carico non porta il nome si resta muti, non si ripiega
 *  sull'identificatore. */
function elementLabel(candidate: Candidate): string | null {
  if (candidate.element_id === null) return null
  if (candidate.element_external_id == null) return null
  const type = candidate.element_type ? ` · ${candidate.element_type}` : ''
  return `${candidate.element_external_id}${type}`
}

/** Il colore del verdetto dato: il rango confermato, o il tono del rifiuto. */
function verdictTone(decided: LastVerdict): string {
  return decided.decision === 'confirmed' ? rankOf(decided.final_level).tone : 'ink-3'
}

/** «Confirmed S3» o «Rejected: vegetation», con le parole del catalogo dei
 *  motivi quando il motivo e' fra quelli ammessi. */
function verdictLabel(
  decided: LastVerdict,
  reasons: Reason[],
  t: (key: MessageKey, values?: Record<string, string | number>) => string,
): string {
  if (decided.decision === 'confirmed') {
    return t('cockpit.decidedConfirmed', { rank: rankOf(decided.final_level).name })
  }
  const reason = reasons.find((one) => one.code === decided.reason_code)
  return t('cockpit.decidedRejected', {
    reason: explainReason(t, decided.reason_code, reason?.label ?? decided.reason_code ?? ''),
  })
}
